/**
 * 本地存储封装
 * 支持 localStorage 和 sessionStorage
 * 支持数据过期时间
 * 支持自动处理 JSON 序列化和反序列化
 */
import { type StorageKey } from '@/shared/constants/storage-keys';

interface StorageData<T> {
  value: T;
  expire: number | null;
}

type StorageType = 'local' | 'session';

class Storage {
  private storage: globalThis.Storage;
  constructor(type: StorageType = 'local') {
    this.storage = type === 'local' ? localStorage : sessionStorage;
  }

  /**
   * 设置存储项
   * @param {STORAGE_KEY} key - 存储的键名
   * @param {T} value - 存储的值（会自动序列化）
   * @param {number} expire - 过期时间（毫秒），可选
   */
  setItem<T>(key: StorageKey, value: T, expire?: number): void {
    const data: StorageData<T> = {
      value,
      expire: expire ? Date.now() + expire : null,
    };
    try {
      this.storage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Storage set error:', error);
    }
  }

  /**
   * 获取存储项
   * @param {STORAGE_KEY} key - 存储的键名
   * @param {T} defaultValue - 默认值，如果获取失败则返回
   */
  getItem<T>(key: StorageKey, defaultValue?: T): T | undefined {
    const item = this.storage.getItem(key);
    if (!item) return defaultValue;

    try {
      const data: StorageData<T> = JSON.parse(item);

      // 检查是否过期
      if (data.expire && data.expire < Date.now()) {
        this.removeItem(key);
        return defaultValue;
      }

      return data.value;
    } catch {
      // 兼容直接存储的非 JSON 数据
      return item as unknown as T;
    }
  }

  /**
   * 删除存储项
   * @param {STORAGE_KEY} key - 要删除的键名
   */
  removeItem(key: StorageKey): void {
    try {
      this.storage.removeItem(key);
    } catch (error) {
      console.error('删除失败:', error);
    }
  }

  /**
   * 清空所有存储项
   */
  clear(): void {
    try {
      this.storage.clear();
    } catch (error) {
      console.error('清空失败:', error);
    }
  }

  /**
    * 检查某个键是否存在
    * @param {STORAGE_KEY} key - 键名
    */
  has(key: StorageKey): boolean {
    return this.storage.getItem(key) !== null;
  }
}

// 导出 localStorage 实例
export const storage = new Storage('local');

// 导出 sessionStorage 实例
export const webSessionStorage = new Storage('session');
